import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { Locale } from '@/api/types';
import {
  I18nContext,
  LOCALE_TAG,
  RTL_LOCALES,
  SUPPORTED_LOCALES,
  type Dir,
  type I18nContextValue,
} from './I18nContext';
import en from './locales/en.json';
import hi from './locales/hi.json';
import ar from './locales/ar.json';

/**
 * Hand-rolled i18n — a locale context + per-locale JSON bundles. No heavy dependency: the whole app
 * needs a `t()` accessor, `{var}` interpolation, an RTL flag, and a switcher; that is ~40 lines.
 *
 * Copy is HUMAN-APPROVED per locale (machine translation is banned for regulated content, F-ED-09).
 * `en` is the authored source of truth. `hi` and `ar` are `_meta.status = "HUMAN_REVIEWED"` —
 * owner-ratified 2026-07-19 via the translation extract sheet (docs/i18n/). Any wording change to
 * hi/ar re-enters the human-review gate: edit → re-ratify → flip `_meta` — never silently.
 */

const BUNDLES: Record<Locale, Record<string, unknown>> = {
  en: en as Record<string, unknown>,
  hi: hi as Record<string, unknown>,
  ar: ar as Record<string, unknown>,
};

function lookup(bundle: Record<string, unknown>, key: string): string | undefined {
  let cur: unknown = bundle;
  for (const part of key.split('.')) {
    if (cur && typeof cur === 'object' && part in (cur as Record<string, unknown>)) {
      cur = (cur as Record<string, unknown>)[part];
    } else {
      return undefined;
    }
  }
  return typeof cur === 'string' ? cur : undefined;
}

function interpolate(s: string, vars?: Record<string, string | number>): string {
  if (!vars) return s;
  return s.replace(/\{(\w+)\}/g, (_m, k: string) => (k in vars ? String(vars[k]) : `{${k}}`));
}

/** Best-effort initial locale for the pre-session gate screens: ?lang → navigator → en. */
function detectInitialLocale(): Locale {
  const url = new URLSearchParams(window.location.search).get('lang');
  if (url && (SUPPORTED_LOCALES as string[]).includes(url)) return url as Locale;
  const nav = (navigator.language || 'en').slice(0, 2);
  if ((SUPPORTED_LOCALES as string[]).includes(nav)) return nav as Locale;
  return 'en';
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocale] = useState<Locale>(detectInitialLocale);
  const dir: Dir = RTL_LOCALES.includes(locale) ? 'rtl' : 'ltr';

  // Mirror the whole document for Arabic; keep lang correct for a11y + font selection.
  useEffect(() => {
    const root = document.documentElement;
    root.lang = LOCALE_TAG[locale];
    root.dir = dir;
  }, [locale, dir]);

  const t = useCallback(
    (key: string, vars?: Record<string, string | number>): string => {
      const s = lookup(BUNDLES[locale], key) ?? lookup(BUNDLES.en, key) ?? key;
      return interpolate(s, vars);
    },
    [locale],
  );

  const value = useMemo<I18nContextValue>(
    () => ({ locale, dir, setLocale, t, localeTag: LOCALE_TAG[locale] }),
    [locale, dir, t],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}
